import React, { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebaseConfig.js";
import styled, { keyframes } from "styled-components";
import {
  PieChart, Pie, Cell, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer,
} from "recharts";
import RefreshIcon from "@mui/icons-material/Refresh";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtGBP  = (n) => `£${parseFloat(n || 0).toFixed(2)}`;
const round   = (n) => Math.round(n * 100) / 100;

const PIE_COLORS_POS_NEG = ["#059669", "#dc2626"];
const PIE_COLORS_EXPENSE  = ["#2563eb", "#7c3aed", "#059669", "#d97706", "#dc2626"];

const EXPENSE_LABELS = { flight: "Flights", hotel: "Hotels", transport: "Transport", visa: "Visa", refund: "Refunds" };

// ─── Custom Tooltip ───────────────────────────────────────────────────────────

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <TooltipBox>
      <TooltipLabel>{payload[0].name}</TooltipLabel>
      <TooltipValue>{fmtGBP(payload[0].value)}</TooltipValue>
    </TooltipBox>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default function FolderBalances() {
  const [positiveFolders, setPositiveFolders] = useState([]);
  const [negativeFolders, setNegativeFolders] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const sp = localStorage.getItem("folderBalances_positive");
    const sn = localStorage.getItem("folderBalances_negative");
    if (sp && sn) {
      setPositiveFolders(JSON.parse(sp));
      setNegativeFolders(JSON.parse(sn));
    }
  }, []);

  const fetchData = async () => {
    setLoading(true);
    const folderMap = {};
    const ensureFolder = (fn) => {
      if (!folderMap[fn]) folderMap[fn] = { folderNo: fn, credit: 0, flight: 0, hotel: 0, transport: 0, visa: 0, refund: 0, invoiced: 0 };
    };
    const processCollection = async (col, field, key) => {
      const snap = await getDocs(collection(db, col));
      snap.forEach((doc) => {
        const d = doc.data();
        const fn = d.folderNo;
        const amt = Number(d[field] || 0);
        if (!fn || fn === "-") return;
        ensureFolder(fn);
        folderMap[fn][key] += amt;
      });
    };
    await processCollection("bankPayments",           "paymentGBP", "credit");
    await processCollection("flightSupplierLedger",   "cost",       "flight");
    await processCollection("hotelSupplierLedger",    "cost",       "hotel");
    await processCollection("transportSupplierLedger","cost",       "transport");
    await processCollection("visaSupplierLedger",     "cost",       "visa");
    await processCollection("customerRefunds",        "amountGBP",  "refund");
    await processCollection("invoiced",               "amount",     "invoiced");

    const positive = [], negative = [];
    Object.values(folderMap).forEach((f) => {
      const totalDebits = f.flight + f.hotel + f.transport + f.visa + f.refund + f.invoiced;
      const balance = round(f.credit - totalDebits);
      if (balance === 0) return;
      const row = { ...f, totalDebits, balance };
      balance > 0 ? positive.push(row) : negative.push(row);
    });
    localStorage.setItem("folderBalances_positive", JSON.stringify(positive));
    localStorage.setItem("folderBalances_negative", JSON.stringify(negative));
    setPositiveFolders(positive);
    setNegativeFolders(negative);
    setLoading(false);
  };

  // ── Chart data ────────────────────────────────────────────────────────────

  const totalProfit = positiveFolders.reduce((s, f) => s + f.balance, 0);
  const totalLoss   = Math.abs(negativeFolders.reduce((s, f) => s + f.balance, 0));
  const netBalance  = totalProfit - totalLoss;
  const allFolders  = [...positiveFolders, ...negativeFolders];

  const pieData = [
    { name: "Positive", value: round(totalProfit) },
    { name: "Negative", value: round(totalLoss) },
  ];

  const expenseData = ["flight", "hotel", "transport", "visa", "refund"].map((key) => ({
    name: EXPENSE_LABELS[key],
    value: round(allFolders.reduce((s, f) => s + (f[key] || 0), 0)),
  }));

  const topLoss = [...negativeFolders]
    .sort((a, b) => a.balance - b.balance)
    .slice(0, 15)
    .map((f) => ({ name: f.folderNo, value: Math.abs(f.balance) }));

  // ── Table renderer ────────────────────────────────────────────────────────

  const renderTable = (data, title, isPositive) => {
    const totalBalance = data.reduce((s, f) => s + (f.balance || 0), 0);
    const accent = isPositive ? "#059669" : "#dc2626";
    const lightBg = isPositive ? "#f0fdf4" : "#fef2f2";

    return (
      <TableSection>
        <SectionHeaderBar accent={accent}>
          <SectionLeft>
            <SectionIconWrap accent={accent}>
              {isPositive ? <TrendingUpIcon style={{ fontSize: 18 }} /> : <TrendingDownIcon style={{ fontSize: 18 }} />}
            </SectionIconWrap>
            <span>{title}</span>
          </SectionLeft>
          <SectionRight>
            <SectionPill accent={accent}>{data.length} folders</SectionPill>
            <SectionTotal accent={accent}>{fmtGBP(totalBalance)}</SectionTotal>
          </SectionRight>
        </SectionHeaderBar>

        <TableWrapOuter light={lightBg}>
          <table className="main-table">
            <thead>
              <tr>
                {["#", "Folder No", "Flights", "Hotels", "Transport", "Refund", "Invoiced", "Credits", "Total Debits", "Balance"].map((col) => (
                  <th key={col}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr><td colSpan={10} className="empty">No folders found</td></tr>
              ) : data.map((f, idx) => {
                const rowFlag = f.invoiced > 0
                  ? (f.balance < 0 ? "invoiced-red" : "invoiced-amber")
                  : (idx % 2 === 0 ? "even" : "odd");
                return (
                  <tr key={f.folderNo} className={rowFlag}>
                    <td className="sr">{idx + 1}</td>
                    <td 
                      onClick={() => {
      const url = `/FolderLedger?FolderNumber=${String(f.folderNo)}`;
      window.open(url, "_blank");
    }}
    style={{ cursor: "pointer" }}><FolderChip accent={accent}>
                      <FolderOpenIcon style={{ fontSize: 13, opacity: 0.6 }} />
                      {f.folderNo}
                    </FolderChip></td>
                    <td className="num">{fmtGBP(f.flight)}</td>
                    <td className="num">{fmtGBP(f.hotel)}</td>
                    <td className="num">{fmtGBP(f.transport)}</td>
                    <td className="num">{fmtGBP(f.refund)}</td>
                    <td className="num">{f.invoiced > 0
                      ? <InvoicedBadge>{fmtGBP(f.invoiced)}</InvoicedBadge>
                      : <span style={{ color: "#cbd5e1" }}>—</span>}
                    </td>
                    <td className="num credit">{fmtGBP(f.credit)}</td>
                    <td className="num debit">{fmtGBP(f.totalDebits)}</td>
                    <td>
                      <BalanceBadge positive={f.balance > 0}>
                        {fmtGBP(f.balance)}
                      </BalanceBadge>
                    </td>
                  </tr>
                );
              })}
              <tr className="total-row">
                <td colSpan={9} className="total-label">Total Balance</td>
                <td>
                  <BalanceBadge positive={totalBalance > 0} large>
                    {fmtGBP(totalBalance)}
                  </BalanceBadge>
                </td>
              </tr>
            </tbody>
          </table>
        </TableWrapOuter>
      </TableSection>
    );
  };

  return (
    <Page>
      {/* ── Header ── */}
      <HeaderRow>
        <div>
          <PageTitle>Folder Report</PageTitle>
          <PageSub>Overview of positive and negative folder balances</PageSub>
        </div>
        <FetchButton onClick={fetchData} disabled={loading} loading={loading}>
          {loading
            ? <><Spinner />&nbsp;Fetching…</>
            : <><RefreshIcon style={{ fontSize: 17 }} />&nbsp;Fetch Data</>}
        </FetchButton>
      </HeaderRow>

      {/* ── Summary Cards ── */}
      <CardsRow>
        <SummaryCard color="#059669" bg="#f0fdf4" border="#bbf7d0">
          <CardIcon color="#059669"><TrendingUpIcon style={{ fontSize: 22 }} /></CardIcon>
          <CardBody>
            <CardLabel>Positive Folders</CardLabel>
            <CardValue color="#059669">{positiveFolders.length}</CardValue>
            <CardSub>{fmtGBP(totalProfit)} total</CardSub>
          </CardBody>
        </SummaryCard>

        <SummaryCard color="#dc2626" bg="#fef2f2" border="#fecaca">
          <CardIcon color="#dc2626"><TrendingDownIcon style={{ fontSize: 22 }} /></CardIcon>
          <CardBody>
            <CardLabel>Negative Folders</CardLabel>
            <CardValue color="#dc2626">{negativeFolders.length}</CardValue>
            <CardSub>{fmtGBP(totalLoss)} total loss</CardSub>
          </CardBody>
        </SummaryCard>

        <SummaryCard color={netBalance >= 0 ? "#2563eb" : "#dc2626"} bg={netBalance >= 0 ? "#eff6ff" : "#fef2f2"} border={netBalance >= 0 ? "#bfdbfe" : "#fecaca"}>
          <CardIcon color={netBalance >= 0 ? "#2563eb" : "#dc2626"}><AccountBalanceIcon style={{ fontSize: 22 }} /></CardIcon>
          <CardBody>
            <CardLabel>Net Balance</CardLabel>
            <CardValue color={netBalance >= 0 ? "#059669" : "#dc2626"}>{fmtGBP(netBalance)}</CardValue>
            <CardSub>{allFolders.length} total folders</CardSub>
          </CardBody>
        </SummaryCard>
      </CardsRow>

      {/* ── Charts ── */}
      <ChartsRow>
        <ChartCard>
          <ChartTitle>Positive vs Negative</ChartTitle>
          <ChartBody>
            <PieChart width={200} height={200}>
              <Pie data={pieData} dataKey="value" outerRadius={85} innerRadius={45} paddingAngle={3}>
                {PIE_COLORS_POS_NEG.map((c, i) => <Cell key={i} fill={c} />)}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
            <PieLegend>
              {pieData.map((d, i) => (
                <LegendItem key={i}>
                  <LegendDot color={PIE_COLORS_POS_NEG[i]} />
                  <div>
                    <LegendName>{d.name}</LegendName>
                    <LegendVal>{fmtGBP(d.value)}</LegendVal>
                  </div>
                </LegendItem>
              ))}
            </PieLegend>
          </ChartBody>
        </ChartCard>

        <ChartCard>
          <ChartTitle>Issuance Breakdown</ChartTitle>
          <ChartBody>
            <PieChart width={200} height={200}>
              <Pie data={expenseData} dataKey="value" nameKey="name" outerRadius={85} innerRadius={45} paddingAngle={3}>
                {PIE_COLORS_EXPENSE.map((c, i) => <Cell key={i} fill={c} />)}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
            <PieLegend>
              {expenseData.map((d, i) => (
                <LegendItem key={i}>
                  <LegendDot color={PIE_COLORS_EXPENSE[i]} />
                  <div>
                    <LegendName>{d.name}</LegendName>
                    <LegendVal>{fmtGBP(d.value)}</LegendVal>
                  </div>
                </LegendItem>
              ))}
            </PieLegend>
          </ChartBody>
        </ChartCard>

        <ChartCard wide>
          <ChartTitle>Top Loss Folders</ChartTitle>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={topLoss} margin={{ top: 0, right: 0, left: 0, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94a3b8" }} angle={-35} textAnchor="end" />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(v) => `£${v}`} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" fill="#dc2626" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </ChartsRow>

      {/* ── Tables ── */}
      {renderTable(positiveFolders, "Positive Folders", true)}
      {renderTable(negativeFolders, "Negative Folders", false)}
    </Page>
  );
}

// ─── Animations ───────────────────────────────────────────────────────────────

const spin   = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const fadeIn = keyframes`from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; }`;

// ─── Layout ───────────────────────────────────────────────────────────────────

const Page = styled.div`
  padding: 2rem;
  max-width: 1300px;
  margin: 25px auto 0;
  animation: ${fadeIn} 0.3s ease;
`;

const PageTitle = styled.h2`
  font-size: 28px; font-weight: 700; color: #2563eb;
  background: #e0f2fe; display: inline-block;
  padding: 8px 20px; border-radius: 8px;
  margin-bottom: 6px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.08); letter-spacing: 0.5px;
`;
const PageSub = styled.p`font-size: 14px; color: #94a3b8; margin: 4px 0 0;`;

const HeaderRow = styled.div`
  display: flex; justify-content: space-between; align-items: flex-start;
  flex-wrap: wrap; gap: 16px; margin-bottom: 28px;
`;

const FetchButton = styled.button`
  display: flex; align-items: center; gap: 6px;
  padding: 11px 20px;
  background: ${p => p.loading ? "#94a3b8" : "#2563eb"};
  color: white; border: none; border-radius: 8px;
  font-size: 14px; font-weight: 600; cursor: ${p => p.loading ? "not-allowed" : "pointer"};
  transition: background 0.2s, transform 0.15s;
  &:hover:not(:disabled) { background: #1d4ed8; transform: translateY(-1px); }
`;

const Spinner = styled.span`
  width: 14px; height: 14px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: white; border-radius: 50%;
  display: inline-block;
  animation: ${spin} 0.8s linear infinite;
`;

// ─── Summary Cards ────────────────────────────────────────────────────────────

const CardsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px; margin-bottom: 28px;
`;

const SummaryCard = styled.div`
  background: ${p => p.bg};
  border: 1px solid ${p => p.border};
  border-radius: 12px; padding: 18px 20px;
  display: flex; align-items: flex-start; gap: 14px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  transition: transform 0.15s, box-shadow 0.15s;
  &:hover { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(0,0,0,0.08); }
`;
const CardIcon = styled.div`
  width: 42px; height: 42px; border-radius: 10px;
  background: ${p => p.color}18;
  color: ${p => p.color};
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
`;
const CardBody = styled.div`display: flex; flex-direction: column; gap: 2px;`;
const CardLabel = styled.div`font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.6px; color: #64748b;`;
const CardValue = styled.div`font-size: 28px; font-weight: 700; color: ${p => p.color}; font-variant-numeric: tabular-nums;`;
const CardSub   = styled.div`font-size: 12px; color: #94a3b8;`;

// ─── Charts ───────────────────────────────────────────────────────────────────

const ChartsRow = styled.div`
  display: flex; flex-wrap: wrap; gap: 16px; margin-bottom: 32px; align-items: flex-start;
`;

const ChartCard = styled.div`
  background: white;
  border: 0.5px solid #e2e8f0;
  border-radius: 12px; padding: 20px 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  flex: ${p => p.wide ? "2 1 420px" : "1 1 260px"};
  min-width: ${p => p.wide ? "380px" : "260px"};
`;

const ChartTitle = styled.div`
  font-size: 13px; font-weight: 700; color: #1e293b;
  text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 16px;
`;

const ChartBody = styled.div`
  display: flex; align-items: center; gap: 20px; flex-wrap: wrap;
`;

const PieLegend = styled.div`display: flex; flex-direction: column; gap: 10px;`;
const LegendItem = styled.div`display: flex; align-items: center; gap: 8px;`;
const LegendDot  = styled.div`width: 10px; height: 10px; border-radius: 50%; background: ${p => p.color}; flex-shrink: 0;`;
const LegendName = styled.div`font-size: 12px; color: #64748b;`;
const LegendVal  = styled.div`font-size: 13px; font-weight: 700; color: #1e293b;`;

const TooltipBox   = styled.div`background: #1e293b; border-radius: 6px; padding: 8px 12px;`;
const TooltipLabel = styled.div`font-size: 11px; color: #94a3b8; margin-bottom: 2px;`;
const TooltipValue = styled.div`font-size: 13px; font-weight: 700; color: white;`;

// ─── Tables ───────────────────────────────────────────────────────────────────

const TableSection = styled.div`
  margin-bottom: 28px;
  border-radius: 12px;
  border: 0.5px solid #e2e8f0;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  animation: ${fadeIn} 0.3s ease;
`;

const SectionHeaderBar = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 13px 20px;
  background: white;
  border-left: 4px solid ${p => p.accent};
  border-bottom: 0.5px solid #e2e8f0;
`;
const SectionLeft = styled.div`
  display: flex; align-items: center; gap: 10px;
  font-size: 15px; font-weight: 600; color: #1e293b;
`;
const SectionIconWrap = styled.div`
  width: 30px; height: 30px; border-radius: 6px;
  background: ${p => p.accent}18; color: ${p => p.accent};
  display: flex; align-items: center; justify-content: center;
`;
const SectionRight = styled.div`display: flex; align-items: center; gap: 12px;`;
const SectionPill  = styled.span`
  background: ${p => p.accent}12; color: ${p => p.accent};
  border: 1px solid ${p => p.accent}25;
  border-radius: 20px; padding: 2px 10px;
  font-size: 12px; font-weight: 600;
`;
const SectionTotal = styled.span`
  font-size: 16px; font-weight: 700; color: ${p => p.accent};
`;

const TableWrapOuter = styled.div`
  overflow-x: auto;
  background: ${p => p.light || "#fff"};

  .main-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    min-width: 900px;

    thead tr {
      background: white;
      border-bottom: 1px solid #e2e8f0;
    }
    th {
      padding: 9px 12px;
      text-align: left;
      font-size: 11px; font-weight: 600;
      color: #94a3b8; letter-spacing: 0.6px;
      text-transform: uppercase; white-space: nowrap;
    }
    th:first-child { padding-left: 18px; }

    td {
      padding: 10px 12px;
      vertical-align: middle;
      border-bottom: 0.5px solid #f1f5f9;
    }
    td:first-child { padding-left: 18px; }

    tr.even td { background: white; }
    tr.odd  td { background: #fafafa; }
    tr.invoiced-red   td { background: #fef2f2; border-left: 3px solid #dc2626; }
    tr.invoiced-amber td { background: #fffbeb; border-left: 3px solid #f59e0b; }
    tr:hover td { background: #eff6ff !important; transition: background 0.1s; }

    .sr    { color: #cbd5e1; font-size: 11px; width: 32px; }
    .num   { font-variant-numeric: tabular-nums; color: #475569; }
    .credit { color: #2563eb !important; font-weight: 500; }
    .debit  { color: #dc3545 !important; font-weight: 500; }

    .empty {
      text-align: center; padding: 32px;
      color: #94a3b8; font-style: italic; font-size: 13px;
    }

    .total-row td {
      background: #f8fafc !important;
      border-top: 1px solid #e2e8f0;
      border-bottom: none;
    }
    .total-label {
      text-align: right; padding-right: 16px;
      font-size: 12px; font-weight: 600;
      color: #64748b; text-transform: uppercase; letter-spacing: 0.5px;
    }
  }
`;

const FolderChip = styled.span`
  display: inline-flex; align-items: center; gap: 5px;
  background: ${p => p.accent}0f;
  border: 0.5px solid ${p => p.accent}30;
  border-radius: 5px; padding: 3px 9px;
  font-size: 12px; font-weight: 600; font-family: monospace;
  color: ${p => p.accent};
`;

const InvoicedBadge = styled.span`
  display: inline-flex; align-items: center;
  background: #fffbeb; border: 0.5px solid #fcd34d;
  border-radius: 4px; padding: 2px 7px;
  font-size: 11px; font-weight: 600; color: #92400e;
`;

const BalanceBadge = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  background: ${p => p.positive ? "#f0fdf4" : "#fef2f2"};
  border: 1px solid ${p => p.positive ? "#86efac" : "#fca5a5"};
  border-radius: 6px; padding: ${p => p.large ? "4px 12px" : "3px 9px"};
  font-size: ${p => p.large ? "14px" : "12px"};
  font-weight: 700;
  color: ${p => p.positive ? "#059669" : "#dc2626"};
  font-variant-numeric: tabular-nums;
`;